import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import type { CompetitionIntroConfig } from '../../../src/competitions/types/competitionIntro.types';
import {
  COMPETITION_WELCOME_ANIM,
  COMPETITION_WELCOME_PHASES,
  COMPETITION_WELCOME_PHASE_LAST,
} from './CompetitionWelcomeScreen.constants';
import { getCompetitionWelcomeStyles } from './CompetitionWelcomeScreen.styles';
import { CompetitionWelcomeSplashPhase } from './CompetitionWelcomeSplashPhase';
import { CompetitionWelcomeRulesPhase } from './CompetitionWelcomeRulesPhase';
import { CompetitionWelcomePointsPhase } from './CompetitionWelcomePointsPhase';

export const COMPETITION_WELCOME_TEST_IDS = {
  screen: 'competition-welcome-screen',
  skip: 'competition-welcome-skip',
  cta: 'competition-welcome-cta',
} as const;

export interface CompetitionWelcomeScreenProps {
  content: CompetitionIntroConfig;
  /** Competition name for the splash hero; falls back to the rules title. */
  title?: string;
  onEnter: () => void;
  testID?: string;
}

/**
 * Per-competition "how it works" (specs/competitions-hub-client Slice 3D, restyled 3G).
 *
 * Same three-beat structure as the World Cup welcome (splash → rules → points) and the
 * same visual language — mascot hero, sketch-framed cards, staggered entrance — but
 * driven entirely by a content config. The WC keeps its own richer screen: that one
 * embeds live MatchCard/FuturesCard previews and App Store 5.2.1 branding logic, so it
 * stays untouched as the reference implementation.
 */
export const CompetitionWelcomeScreen: React.FC<CompetitionWelcomeScreenProps> = ({
  content,
  title,
  onEnter,
  testID = COMPETITION_WELCOME_TEST_IDS.screen,
}) => {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(
    () =>
      getCompetitionWelcomeStyles(theme, {
        top: insets.top,
        bottom: insets.bottom,
        right: insets.right,
      }),
    [theme, insets.top, insets.bottom, insets.right],
  );

  const overlayOpacity = useRef(new Animated.Value(1)).current;
  const outgoingOpacity = useRef(new Animated.Value(0)).current;

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [outgoingPhase, setOutgoingPhase] = useState<number | null>(null);
  const [isExiting, setIsExiting] = useState(false);

  /** Web: stop scroll chaining to the document while the takeover is up. */
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const handleEnterApp = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);
    Animated.timing(overlayOpacity, {
      toValue: 0,
      duration: COMPETITION_WELCOME_ANIM.OVERLAY_EXIT_MS,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: Platform.OS !== 'web',
    }).start(({ finished }) => {
      if (finished) onEnter();
    });
  }, [isExiting, onEnter, overlayOpacity]);

  /**
   * Mount the next phase first, then fade the previous one out on top of it, so the
   * incoming phase's stagger entrance is already running underneath — no mount gap.
   *
   * A tap that lands mid-crossfade supersedes it rather than being dropped: starting a
   * new timing on `outgoingOpacity` cancels the in-flight one. Guarding on "is animating"
   * instead would silently swallow an eager second tap and strand the user on a beat.
   */
  const handleNext = useCallback(() => {
    if (isExiting) return;
    if (phaseIndex >= COMPETITION_WELCOME_PHASE_LAST) {
      handleEnterApp();
      return;
    }

    const nextPhase = phaseIndex + 1;
    setOutgoingPhase(phaseIndex);
    setPhaseIndex(nextPhase);
    outgoingOpacity.setValue(1);

    requestAnimationFrame(() => {
      Animated.timing(outgoingOpacity, {
        toValue: 0,
        duration: COMPETITION_WELCOME_ANIM.PHASE_CROSSFADE_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: Platform.OS !== 'web',
      }).start(({ finished }) => {
        // A superseded fade reports finished: false — the newer one owns the cleanup.
        if (finished) setOutgoingPhase(null);
      });
    });
  }, [handleEnterApp, isExiting, outgoingOpacity, phaseIndex]);

  const scrollContentPaddingTop = insets.top + 56;

  /**
   * Only the active phase carries test IDs: during a crossfade the outgoing phase is
   * still mounted, and duplicate IDs would make `getByTestId` ambiguous.
   */
  const renderPhase = (index: number, isActive: boolean) => {
    const shared = {
      content,
      phaseIndex: index,
      phaseCount: COMPETITION_WELCOME_PHASES.length,
      isExiting,
      bottomInset: insets.bottom,
      onNext: handleNext,
      ctaTestID: isActive ? COMPETITION_WELCOME_TEST_IDS.cta : undefined,
    };

    if (index === 0) {
      return (
        <CompetitionWelcomeSplashPhase
          content={content}
          title={title ?? content.rules.title}
          isExiting={isExiting}
          topInset={insets.top}
          bottomInset={insets.bottom}
          phaseIndex={index}
          phaseCount={COMPETITION_WELCOME_PHASES.length}
          onNext={handleNext}
          ctaTestID={shared.ctaTestID}
          testID={isActive ? `${testID}-splash` : undefined}
        />
      );
    }
    if (index === 1) {
      return (
        <CompetitionWelcomeRulesPhase
          {...shared}
          scrollContentPaddingTop={scrollContentPaddingTop}
          testID={isActive ? `${testID}-rules` : undefined}
        />
      );
    }
    return (
      <CompetitionWelcomePointsPhase
        {...shared}
        scrollContentPaddingTop={scrollContentPaddingTop}
        testID={isActive ? `${testID}-points` : undefined}
      />
    );
  };

  return (
    <Animated.View
      style={[styles.root, { opacity: overlayOpacity }]}
      pointerEvents="auto"
      testID={testID}
    >
      <View style={styles.phaseLayer}>{renderPhase(phaseIndex, true)}</View>

      {outgoingPhase !== null && outgoingPhase !== phaseIndex ? (
        <Animated.View
          style={[StyleSheet.absoluteFillObject, { opacity: outgoingOpacity }]}
          pointerEvents="none"
        >
          {renderPhase(outgoingPhase, false)}
        </Animated.View>
      ) : null}

      <View style={styles.skipWrap} pointerEvents="box-none">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={content.skip}
          disabled={isExiting}
          hitSlop={12}
          onPress={handleEnterApp}
          style={({ pressed, hovered }) => [
            styles.skipPressable,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && !isExiting && styles.skipPressablePressed,
            hovered && Platform.OS === 'web' && styles.skipPressableHovered,
          ]}
          testID={COMPETITION_WELCOME_TEST_IDS.skip}
        >
          <Text style={styles.skipLabel}>{content.skip}</Text>
        </Pressable>
      </View>
    </Animated.View>
  );
};
